import { getBinaryTargetForCurrentPlatform } from "@prisma/internals";
import {
  ConnectorType,
  IDMMF,
  IPrismaClientRequestRPC,
  IPrismaClientRequestRPCQuery,
  PrismaOperation,
} from "@prisma/studio-common";
import { getPrismaClient } from "./client";
import { getDMMF } from "./dmmf";
import { getGenerator } from "./generators";
import {
  ResolveOverrides,
  guessEnginePaths,
  guessPrismaClientPath,
} from "./paths";
import { debug } from "./utils/debug";
import { hashSchema } from "./utils/hashSchema";
import { loadEnvironment } from "./utils/loadEnvironment";
import { PrismaEngineType } from "./utils/prismaEngineName";
import type { DriverAdapter } from "@prisma/driver-adapter-utils";

class PCW {
  private schema: string;
  public schemaPath: string;

  private adapter?: DriverAdapter;

  /** Additional env vars to inject into process.env */
  private env: Record<string, string>;
  private resolveOverrides: ResolveOverrides;
  /** Disregard schema, env vars etc. and use the QE Binary */
  private forcePrismaBinary: boolean;
  /** Disregard schema, env vars etc. and use the QE Library */
  private forcePrismaLibrary: boolean;
  /** Disallow write operations on the generated Prisma Client */
  private readOnly: boolean;

  /** Cached stuff */
  private resolvedPrismaEngines?: {
    queryEngine?: { type: PrismaEngineType; path: string };
  };
  private dmmf?: IDMMF;
  private datasourceProvider?: ConnectorType;
  private datasources?: Record<string, { url?: string }>;
  private previewFeatures?: string[];
  private directUrl?: string;
  private prismaClient?: {
    prisma: any;
    PrismaClient: any;
    PrismaClientKnownRequestError: any;
    PrismaClientRustPanicError: any;
    PrismaClientInitializationError: any;
    PrismaClientValidationError: any;
  };
  private versions?: {
    prisma?: string;
    queryEngine?: string;
  };

  /**
   * Initializes PCW
   *
   * @param schema The Prisma schema string
   * @param schemaPath Location of the Prisma Schema on disk (required temporarily for in-memory Client)
   * @param config The PrismaConfig loaded from the prisma.config.js file
   * @param env KV-pair of environment variables used in the Prisma Schema
   * @param options
   * @param options.resolve Path to a custom prisma client / engines module to use
   * @param options.readOnly Controls if this PCW instance should block mutating requests
   */
  constructor(
    schema: string,
    schemaPath: string,
    env: Record<string, string> = {},
    options: {
      /**
       * Data sources to pass to the Prisma Client constructor.
       */
      datasources?: Record<string, { url?: string }>;
      resolve?: {
        "@prisma/engines"?: string;
        "@prisma/client"?: string;
        ".prisma/client"?: string;
      };
      forcePrismaBinary?: boolean;
      forcePrismaLibrary?: boolean;
      readOnly?: boolean;
      directUrl?: string;
    } = {},
    adapter?: DriverAdapter,
    versions?: {
      prisma?: string;
      queryEngine?: string;
    }
  ) {
    debug("[constructor]", options);
    this.schema = schema;
    this.schemaPath = schemaPath;
    this.adapter = adapter;
    this.env = { ...env };
    this.resolveOverrides = options.resolve || {};
    this.forcePrismaBinary = !!options.forcePrismaBinary;
    this.forcePrismaLibrary = !!options.forcePrismaLibrary;
    this.readOnly = !!options.readOnly;
    this.datasources = options.datasources;
    this.directUrl = options.directUrl;
    this.versions = versions;

    if (this.forcePrismaLibrary && this.forcePrismaBinary) {
      throw new Error(
        "Invalid params: `forcePrismaBinary` and `forcePrismaLibrary` cannot both be truthy"
      );
    }

    // Setup process.env
    if (this.forcePrismaLibrary) {
      // We just set this manually so that `getGenerator` knows to download the library
      this.env.PRISMA_CLIENT_ENGINE_TYPE = "library";
    } else if (this.forcePrismaBinary) {
      // We just set this manually so that `getGenerator` knows to download the binary
      this.env.PRISMA_CLIENT_ENGINE_TYPE = "binary";
    }

    // Do not use the spread operator here. `process.env` is special in some sense I don't understand yet.
    // Spreading it breaks `open` on Prisma CLI.
    Object.assign(process.env, this.env);
  }

  get schemaHash() {
    return hashSchema(this.schema);
  }

  /**
   * Returns path to Prisma binaries, taking `resolve`s into account, and defaulting to a hard-coded path
   */
  private async resolvePrismaEngines(): Promise<
    NonNullable<PCW["resolvedPrismaEngines"]>
  > {
    // Try returning from cache first
    if (this.resolvedPrismaEngines) {
      return this.resolvedPrismaEngines;
    }

    const { queryEngine } = await guessEnginePaths({
      forceBinary: this.forcePrismaBinary,
      forceLibrary: this.forcePrismaLibrary,
      resolveOverrides: this.resolveOverrides,
    });

    // Cache this for the next time
    this.resolvedPrismaEngines = {
      queryEngine,
    };

    return this.resolvedPrismaEngines;
  }

  /**
   * Returns a path to a Prisma Client package, taking `resolve`s into account, and defaulting to a hard-coded path
   */
  private resolvePrismaClient(): string {
    return guessPrismaClientPath({ resolveOverrides: this.resolveOverrides });
  }

  /**
   * Parse the given Prisma schema string as an AST (DMMF)
   *
   * @param options
   * @param options.binaryPathsOverride Override the paths of the Prisma binaries used
   * @param options.binaryPathsOverride.queryEngine Path to the query engine binary
   */
  public getDMMF = async (): Promise<{
    dmmf: IDMMF;
    datasourceProvider: ConnectorType;
    previewFeatures: string[];
  }> => {
    // Try returning from cache first
    if (this.dmmf && this.datasourceProvider && this.previewFeatures) {
      return Promise.resolve({
        dmmf: this.dmmf,
        datasourceProvider: this.datasourceProvider,
        previewFeatures: this.previewFeatures,
      });
    }

    try {
      await loadEnvironment(this.schemaPath);
      const prismaClientPath = this.resolvePrismaClient();
      const { queryEngine } = await this.resolvePrismaEngines();

      debug("[getDMMF] Calling getGenerator with:", {
        queryEngine,
        prismaClientPath,
      });

      const prismaClientGenerator = await getGenerator({
        schemaPath: this.schemaPath,
        forceBinary: this.forcePrismaBinary,
        forceLibrary: this.forcePrismaLibrary,
        paths: {
          queryEngine,
          prismaClient: prismaClientPath,
        },
      });

      if (!this.forcePrismaBinary && !this.forcePrismaLibrary) {
        // If none of the `force` flags are set, override `resolvedPrismaEngines` cache with whatever `getGenerators` returned

        const binaryTarget = await getBinaryTargetForCurrentPlatform();
        let resolvedQueryEngineType: PrismaEngineType;
        let resolvedQueryEnginePath: string;

        // The types from the @prisma/internals package account for all conditions
        // The way we're using the generators makes `!` fine in this block
        if (prismaClientGenerator.options!.binaryPaths!.queryEngine) {
          resolvedQueryEngineType = "binary";
          resolvedQueryEnginePath =
            prismaClientGenerator.options!.binaryPaths!.queryEngine[
              binaryTarget
            ];
        } else if (prismaClientGenerator.options!.binaryPaths!.libqueryEngine) {
          resolvedQueryEngineType = "library";
          resolvedQueryEnginePath =
            prismaClientGenerator.options!.binaryPaths!.libqueryEngine[
              binaryTarget
            ];
        } else {
          throw new Error(
            "Unable to resolve Prisma Query Engine. This is a bug."
          );
        }

        // Override existing cache with what `getGenerator` returned
        this.resolvedPrismaEngines = {
          queryEngine: {
            type: resolvedQueryEngineType,
            path: resolvedQueryEnginePath,
          },
        };
      }

      const { dmmf, datasourceProvider, previewFeatures } = getDMMF({
        generator: prismaClientGenerator,
        forceBinary: this.forcePrismaBinary,
        forceLibrary: this.forcePrismaLibrary,
        paths: {
          prismaClient: prismaClientPath,
        },
      });

      // Cache these things so you don't have to compute these over and over
      this.dmmf = dmmf;
      this.datasourceProvider = datasourceProvider;
      this.previewFeatures = previewFeatures;

      prismaClientGenerator.stop();

      debug("[getDMMF] finished", {
        prismaClientPath,
        prismaEngines: this.resolvedPrismaEngines,
        previewFeatures,
      });
    } catch (e) {
      console.error("Unable to get DMMF from Prisma Client: ", e);

      throw e;
    }

    return {
      dmmf: this.dmmf,
      datasourceProvider: this.datasourceProvider,
      previewFeatures: this.previewFeatures,
    };
  };

  /**
   * Get an in-memory instance of Prisma Client
   */
  public async getPrismaClient(): Promise<{
    prisma: any;
    PrismaClient: any;
    PrismaClientKnownRequestError: any;
    PrismaClientRustPanicError: any;
    PrismaClientInitializationError: any;
    PrismaClientValidationError: any;
  }> {
    const { dmmf, datasourceProvider, previewFeatures } = await this.getDMMF();
    const { queryEngine } = await this.resolvePrismaEngines();
    const prismaClientPath = this.resolvePrismaClient();

    // Try returning from cache first
    if (this.prismaClient) return this.prismaClient;

    const {
      prisma,
      PrismaClient,
      PrismaClientKnownRequestError,
      PrismaClientRustPanicError,
      PrismaClientInitializationError,
      PrismaClientValidationError,
    } = await getPrismaClient({
      schema: this.schema,
      schemaPath: this.schemaPath,
      dmmf,
      adapter: this.adapter,
      engineType: queryEngine?.type ?? "library",
      datasourceProvider,
      datasources: this.datasources,
      previewFeatures,
      paths: {
        queryEngine,
        prismaClient: prismaClientPath,
      },
      directUrl: this.directUrl,
      versions: this.versions,
    });

    // Cache these things so you don't have to compute these over and over
    this.prismaClient = {
      prisma,
      PrismaClient,
      PrismaClientKnownRequestError,
      PrismaClientRustPanicError,
      PrismaClientInitializationError,
      PrismaClientValidationError,
    };

    return this.prismaClient;
  }

  /**
   * Send a Prisma query to the binary. Resolves with the response.
   *
   * @param options.query
   */
  public request = async (
    query: IPrismaClientRequestRPC["request"]["payload"]["data"],
    { prisma }: { prisma?: any } = {}
  ): Promise<IPrismaClientRequestRPC["response"]["payload"]["data"]> => {
    if (!prisma) {
      prisma = (await this.getPrismaClient()).prisma;
    }

    try {
      // Construct the Prisma Client query from its parts
      let prismaClientResponse;
      if (query.operation === "$transaction") {
        prismaClientResponse = await prisma.$transaction(
          query.queries.map(q => this._request(prisma, q))
        );
      } else {
        prismaClientResponse = await this._request(prisma, query);
      }

      return prismaClientResponse;
    } catch (e) {
      // Re-throw errors so consumer can catch it
      throw e;
    } finally {
      // Always disconnect after queries
      await prisma.$disconnect();
    }
  };

  private _request(prisma: any, query: IPrismaClientRequestRPCQuery) {
    const READONLY_PRISMA_OPERATIONS: PrismaOperation[] = [
      "findUnique",
      "findMany",
      "findFirst",
      "count",
      "aggregate",
      "groupBy",
    ];

    if (!query.modelName) {
      throw new Error("Invalid Prisma Clinet query");
    }

    // Prisma Client's methods are not === the actual model name. For example, when the model has a name that starts with an uppercase alphabet, it is lowercased
    // So we calculate this "transformed" model name to be able to access the correct key in the `prisma` object.
    const prismaClientModel =
      query.modelName.charAt(0).toLowerCase() + query.modelName.slice(1);

    if (!(prismaClientModel in prisma)) {
      throw new Error(`No model in schema with name \`${query.modelName}\``);
    }

    // Block the Prisma Client request if a mutating request comes in in readOnly mode
    if (
      this.readOnly &&
      !READONLY_PRISMA_OPERATIONS.includes(query.operation)
    ) {
      throw new Error("You are not permitted to perform this action");
    }

    return prisma[prismaClientModel][query.operation].call(null, query.args);
  }
}

export default PCW;
